import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useLocation, useNavigate } from 'react-router-dom';
import gsap from 'gsap';
import '../styles/Navbar.css';

const links = [
  { id: 'why', label: 'Why CVEarity' },
  { id: 'process', label: 'Our Process' },
  { id: 'cve-lookup', label: 'CVE Lookup' },
  { id: 'news', label: 'Security News' },
  { id: 'faq', label: 'FAQ' },
  { id: 'blogs', label: 'Blogs', external: true, url: 'https://blogs.cvearity.com' },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState('home');
  const navRef = useRef(null);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    if (location.pathname === '/' && location.hash) {
      setTimeout(() => {
        document.getElementById(location.hash.slice(1))?.scrollIntoView({ behavior: 'smooth' });
      }, 100);
    }
  }, [location.pathname, location.hash]);

  useEffect(() => {
    const onScroll = () => {
      setScrolled(window.scrollY > 60);
      if (location.pathname !== '/') return;
      const ids = ['home', 'why', 'process', 'cve-lookup', 'news', 'faq', 'contact'];
      for (let i = ids.length - 1; i >= 0; i--) {
        const el = document.getElementById(ids[i]);
        if (el && window.scrollY >= el.offsetTop - 140) {
          setActive(ids[i]);
          break;
        }
      }
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, [location.pathname]);

  const scrollTo = (id) => {
    if (location.pathname !== '/') {
      navigate('/#' + id);
    } else {
      document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
    }
    setOpen(false);
  };

  const handleLinkHover = (e) => {
    gsap.to(e.currentTarget, {
      y: -1,
      duration: 0.2,
      ease: 'power2.out',
    });
  };

  const handleLinkLeave = (e) => {
    gsap.to(e.currentTarget, {
      y: 0,
      duration: 0.2,
      ease: 'power2.out',
    });
  };

  return (
    <>
      <nav className={`navbar ${scrolled ? 'navbar--scrolled' : ''}`} ref={navRef}>
        <div className="navbar__inner">
          <button className="navbar__logo" onClick={() => {
            if (location.pathname !== '/') {
              navigate('/');
            }
            setTimeout(() => window.scrollTo({ top: 0, behavior: 'smooth' }), 50);
          }}>
            <img
              src={process.env.PUBLIC_URL + '/CVEARITY1.png'}
              alt="CVEarity"
              className="navbar__logo-img"
            />
          </button>

          <nav className="navbar__nav">
            {links.map(l => (
              l.external ? (
                <a
                  key={l.id}
                  href={l.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="navbar__link"
                  onMouseEnter={handleLinkHover}
                  onMouseLeave={handleLinkLeave}
                >
                  {l.label}
                </a>
              ) : (
                <button
                  key={l.id}
                  className={`navbar__link ${active === l.id ? 'navbar__link--active' : ''}`}
                  onClick={() => scrollTo(l.id)}
                  onMouseEnter={handleLinkHover}
                  onMouseLeave={handleLinkLeave}
                >
                  {l.label}
                </button>
              )
            ))}
          </nav>

          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <a
              href="https://calendly.com/cvearity"
              target="_blank"
              rel="noopener noreferrer"
              className="btn-primary navbar__cta"
            >
              Book a Demo
              <svg width="13" height="13" viewBox="0 0 13 13" fill="none">
                <path d="M2 11L11 2M11 2H6M11 2V7" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
              </svg>
            </a>
            <button
              className={`navbar__toggle ${open ? 'navbar__toggle--open' : ''}`}
              onClick={() => setOpen(!open)}
              aria-label="Toggle menu"
            >
              <span className="navbar__bar" />
              <span className="navbar__bar" />
              <span className="navbar__bar" />
            </button>
          </div>
        </div>
      </nav>

      <AnimatePresence>
        {open && (
          <motion.div
            className="navbar__mobile"
            initial={{ opacity: 0, y: -12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -12 }}
            transition={{ duration: 0.18 }}
          >
            {links.map((l, i) => (
              l.external ? (
                <motion.a
                  key={l.id}
                  href={l.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="navbar__mobile-link"
                  initial={{ opacity: 0, x: -12 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: i * 0.04 }}
                >
                  {l.label}
                </motion.a>
              ) : (
                <motion.button
                  key={l.id}
                  className={`navbar__mobile-link ${active === l.id ? 'navbar__mobile-link--active' : ''}`}
                  onClick={() => scrollTo(l.id)}
                  initial={{ opacity: 0, x: -12 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: i * 0.04 }}
                >
                  {l.label}
                </motion.button>
              )
            ))}
            <a
              href="https://calendly.com/cvearity"
              target="_blank"
              rel="noopener noreferrer"
              className="btn-primary navbar__mobile-cta"
            >
              Book a Demo
            </a>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
